import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useNextT } from "../i18n";
import s from "./public.module.scss";

/** English and Hebrew only for now (owner, P1.5); the names are always written in their own language. */
export const LANGUAGES = [{ code: "en", name: "English", short: "EN" }, { code: "he", name: "עברית", short: "HE" }] as const;

/** The compact language menu of every public page (board 206): a globe, the current code and a chevron; opens a short list. */
export const LanguageMenu = ({ onChange }: { onChange?: (code: string) => void }) => {
  const { i18n } = useTranslation();
  const { t } = useNextT();
  const current = LANGUAGES.find((l) => l.code === (i18n.language || "en").slice(0, 2)) ?? LANGUAGES[0];
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const pick = (code: string) => {
    setOpen(false);
    button.current?.focus();
    if (code === current.code) return;
    void i18n.changeLanguage(code);
    try { localStorage.setItem("language", code); } catch { /* storage blocked: the choice lasts this visit */ }
    onChange?.(code);
  };

  return (
    <div className={s.lang} ref={root} onKeyDown={(e) => { if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); button.current?.focus(); } }}>
      <button ref={button} type="button" className={s.langBtn} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined}
        aria-label={`${t("public.language")}: ${current.name}`} onClick={() => setOpen((o) => !o)}>
        <LanguageRoundedIcon aria-hidden fontSize="small" />
        <span aria-hidden="true">{current.short}</span>
        <ExpandMoreRoundedIcon aria-hidden fontSize="small" />
      </button>
      {open && (
        <ul id={id} role="menu" aria-label={t("public.language")} className={s.langList}>
          {LANGUAGES.map((l) => (
            <li key={l.code} role="none">
              <button type="button" role="menuitemradio" aria-checked={l.code === current.code} lang={l.code} className={s.langItem} onClick={() => pick(l.code)} autoFocus={l.code === current.code}>
                <span>{l.name}</span>{l.code === current.code && <CheckRoundedIcon aria-hidden fontSize="small" />}
              </button>
            </li>
          ))}
          {/* Board 206 / walkthrough #35: a quiet line, not a choice. */}
          <li role="menuitem" aria-disabled="true" className={s.langMore}>{t("public.more_languages")}</li>
        </ul>
      )}
    </div>
  );
};
